import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const ValidationStatusBanner = ({ status, validationResult, isAdmin, onSave, isSaved }) => {
    if (!status) return null;

    const isGo = status === 'go' || status === 'GO';

    const isNoGo = status === 'no_go' || status === 'NO_GO';

    if (!isGo && !isNoGo) return null;

    // Format stats from validationResult
    const duration = validationResult?.totalDurationMs;
    const tokens = validationResult?.totalTokens || 0;
    const cost = validationResult?.estimatedCostUsd;
    const validatedAt = validationResult?.validatedAt;

    // Debug: Log what we receive for validatedAt
    console.log('[ValidationStatusBanner] validatedAt:', validatedAt, 'type:', typeof validatedAt, 'isArray:', Array.isArray(validatedAt));

    const formatDuration = (ms) => {
        if (!ms) return null;
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    const formatCost = (usd) => {
        if (!usd) return null;
        if (usd < 0.01) return `$${(usd * 100).toFixed(2)}¢`;
        return `$${usd.toFixed(4)}`;
    };

    const formatValidatedAt = (timestamp) => {
        if (!timestamp) return null;
        try {
            // Handle different timestamp formats (epoch ms, epoch s, ISO string, Java LocalDateTime array)
            let date;
            if (Array.isArray(timestamp)) {
                // Java LocalDateTime serializes as array: [year, month, day, hour, minute, second, nano]
                // Note: JavaScript months are 0-indexed, Java months are 1-indexed
                const [year, month, day, hour = 0, minute = 0, second = 0] = timestamp;
                date = new Date(year, month - 1, day, hour, minute, second);
            } else if (typeof timestamp === 'number') {
                // If it's a small number, it's probably seconds, not ms
                date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
            } else {
                date = new Date(timestamp);
            }

            // Check if date is valid
            if (isNaN(date.getTime())) return null;

            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

            if (diffMins < 1) return 'just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;

            // For older than 24h, show date and time
            return date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return null;
        }
    };

    const formattedDate = formatValidatedAt(validatedAt);
    const statsContent = (
        <div className="pipeline-stats">
            {formattedDate && <span className="stat-item">📅 {formattedDate}</span>}
            {duration && <span className="stat-item">⏱️ {formatDuration(duration)}</span>}
            {isAdmin && tokens > 0 && <span className="stat-item">🔤 {tokens} tokens</span>}
            {isAdmin && cost > 0 && <span className="stat-item">💰 {formatCost(cost)}</span>}
        </div>
    );

    const saveButton = onSave && (
        <button
            className={`banner-save-btn ${isSaved ? 'saved' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                if (!isSaved) onSave();
            }}
            title={isSaved ? "Already saved to journal" : "Save to Trading Journal"}
            disabled={isSaved}
        >
            {isSaved ? <CheckCircleIcon fontSize="small" /> : <MenuBookIcon fontSize="small" />}
            <span>{isSaved ? 'Saved' : 'Save to Journal'}</span>
        </button>
    );

    if (isGo) {
        return (
            <div className="pipeline-status-banner status-go">
                <div className="status-main">
                    <CheckCircleIcon />
                    <span>PASSED</span>
                </div>
                {statsContent}
                {saveButton}
            </div>
        );
    }

    return (
        <div className="pipeline-status-banner status-no-go">
            <div className="status-main">
                <CancelIcon />
                <span>MISMATCH</span>
            </div>
            {statsContent}
            {saveButton}
        </div>
    );
};

export default ValidationStatusBanner;
